// Custom error types
class TypesenseNetworkError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'TypesenseNetworkError'
  }
}

class TypesenseServerError extends Error {
  constructor(message: string, public status: number) {
    super(message)
    this.name = 'TypesenseServerError'
  }
}

class TypesenseTimeoutError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'TypesenseTimeoutError'
  }
}

export interface TypesenseConfig {
  nodes: string[]
  protocol: 'http' | 'https'
}

export interface SearchParameters {
  q: string
  query_by?: string
  facet_by?: string
  sort_by?: string
  per_page?: string
  [key: string]: string | undefined
}

export interface Order {
  id: string
  imageUrl: string
  locations: Location[] | null
  name: string
  numberOfShoots: number
  numberOfVideos: number
  orderId: string
  paymentType: string
  state: string
  videoProjectPrice: number
  createdAt: number
  dueDate: number
  organisations: ControllingOrganisation[]
  orderType: 'vaas' | 'order'
}

export interface ControllingOrganisation {
  id: string
  name: string
  city: string
  country: string
  countryCode: string
  region: string
  imageUrl: string
}
export interface Location {
  address: string
  cityId: string
  cityName: string
  citySubdivisionCode: string
  citySubdivisionName: string
  countryCode: string
  countryGroup: string
  countryId: string
  countryName: string
  lat: number
  long: number
}

export interface ListProps {
  id: string
  name: string
  checked?: boolean
  disabled?: boolean
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void | undefined
}

const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))

export const typesenseQuery = async (
  apiKey: string,
  tsConfig: TypesenseConfig,
  collectionName: string,
  searchParameters: SearchParameters,
  maxRetries: number = 3,
  initialRetryDelay: number = 1000,
  timeout: number = 5000
): Promise<any> => {
  const { nodes, protocol } = tsConfig

  const fetchWithRetry = async (
    url: string,
    options: RequestInit,
    retriesLeft: number
  ): Promise<any> => {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), timeout)

    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
      })
      clearTimeout(timeoutId)

      if (!response.ok) {
        if (response.status >= 500) {
          throw new TypesenseServerError(`HTTP error! status: ${response.status}`, response.status)
        } else {
          throw new Error(`HTTP error! status: ${response.status}`)
        }
      }
      return await response.json()
    } catch (error) {
      clearTimeout(timeoutId)
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw new TypesenseTimeoutError(`Request timed out after ${timeout}ms`)
      }
      if (error instanceof TypeError) {
        throw new TypesenseNetworkError('Network error occurred')
      }
      if (retriesLeft === 0) {
        throw error
      }
      // tslint:disable-next-line:no-console
      console.log(`Retrying... Attempts left: ${retriesLeft}`)
      const retryDelay = initialRetryDelay * 2 ** (maxRetries - retriesLeft)
      await delay(retryDelay)
      return fetchWithRetry(url, options, retriesLeft - 1)
    }
  }
  const queryParams: Record<string, string> = Object.entries(searchParameters).reduce(
    (acc: Record<string, string>, [key, value]) => {
      if (value !== undefined) {
        acc[key] = value
      }
      return acc
    },
    {}
  )

  const queryString = new URLSearchParams(queryParams).toString()

  for (const node of nodes) {
    const url = `${protocol}://${node}/collections/${collectionName}/documents/search?${queryString}`
    const options: RequestInit = {
      method: 'GET',
      headers: {
        'X-TYPESENSE-API-KEY': apiKey,
        'Content-Type': 'application/json',
      },
    }

    try {
      return await fetchWithRetry(url, options, maxRetries)
    } catch (error) {
      console.error(`Error querying Typesense node ${node}:`, error)
    }
  }

  throw new Error('All Typesense nodes failed to respond')
}

const TSnode = '7gtkcyvzs1xa4pqep-1.a1.typesense.net'

// Usage example
export const config: TypesenseConfig = {
  nodes: [TSnode],
  protocol: 'https',
}



// WEBPACK FOOTER //
// ./src/typeSense.tsx